import { createContext, useContext, useState, useEffect } from "react"
import translations from "../data/translations.json"
import { detectUserLanguage, checkGeolocationPermission } from "../utils/geolocation"

const LanguageContext = createContext()

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

export const LanguageProvider = ({ children }) => {
  // Initialize language from localStorage or default to "en"
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('selectedLanguage') || "en"
    }
    return "en"
  })

  // Track if GPS language detection has been attempted
  const [gpsDetectionAttempted, setGpsDetectionAttempted] = useState(false)
  
  // Track if user has manually selected a language
  const [userHasManuallySelected, setUserHasManuallySelected] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('userManualLanguageSelection') === 'true'
    }
    return false
  })

  // RTL languages list
  const rtlLanguages = ['ar', 'ur', 'persian', 'pashto']

  const languages = [
    { code: "en", name: "English" },
    { code: "hi", name: "हिंदी" },
    { code: "ar", name: "العربية" },
    { code: "ur", name: "اردو" },
    { code: "fr", name: "Français" },
    { code: "ko", name: "한국어" },
    { code: "filipino", name: "Filipino" },
    { code: "persian", name: "فارسی" },
    { code: "pashto", name: "پښتو" }
  ]

  const t = (key) => {
    const keys = key.split('.')
    let value = translations[currentLanguage]

    // Try to get value from current language
    for (const k of keys) {
      value = value?.[k]
    }

    // If not found, try English as fallback
    if (value === undefined) {
      value = translations.en
      for (const k of keys) {
        value = value?.[k]
      }
    }

    // Return value or the key itself if not found
    return value || key
  }

  // GPS-based language detection on first load
  useEffect(() => {
    const attemptGpsLanguageDetection = async () => {
      console.log('🚀 GPS Detection Check - Starting evaluation...')
      console.log('🔍 Current conditions:')
      console.log('  - Browser environment:', typeof window !== 'undefined')
      console.log('  - GPS detection attempted:', gpsDetectionAttempted)
      console.log('  - User manually selected:', userHasManuallySelected)
      console.log('  - Current language:', currentLanguage)
      console.log('  - Stored language:', localStorage.getItem('selectedLanguage'))
      console.log('  - GPS language detected before:', localStorage.getItem('gpsLanguageDetected'))
      
      // Very relaxed conditions - attempt GPS detection if:
      // 1. We're in browser environment
      // 2. GPS detection hasn't been attempted yet in this session
      // 3. Current language is English (meaning no GPS language is active)
      if (
        typeof window !== 'undefined' && 
        !gpsDetectionAttempted &&
        currentLanguage === 'en'
      ) {
        try {
          setGpsDetectionAttempted(true)
          console.log('✅ Conditions met - Attempting GPS-based language detection...')
          
          console.log('🌍 Starting language detection...')
          const detectedLanguage = await detectUserLanguage()
          console.log('🎯 Language detection result:', detectedLanguage)
          
          if (detectedLanguage && detectedLanguage !== 'en') {
            console.log('✨ GPS detected non-English language:', detectedLanguage)
            setCurrentLanguage(detectedLanguage)
            
            // Save the GPS-detected language but don't mark as manually selected
            localStorage.setItem('selectedLanguage', detectedLanguage)
            localStorage.setItem('gpsLanguageDetected', 'true')
            localStorage.setItem('gpsDetectedLanguage', detectedLanguage)
            console.log('💾 Language saved to localStorage')
          } else {
            console.log('🔄 GPS detection returned English, keeping default')
            // Still mark as attempted to avoid repeated tries
            localStorage.setItem('gpsLanguageDetected', 'true')
          }
        } catch (error) {
          console.warn('❌ GPS language detection failed:', error)
          // Mark as attempted even if failed to avoid repeated failures
          localStorage.setItem('gpsLanguageDetected', 'true')
        }
      } else {
        console.log('⏭️ GPS detection skipped - conditions not met')
      }
    }

    // Run detection after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(attemptGpsLanguageDetection, 1000)
    
    return () => clearTimeout(timeoutId)
  }, [gpsDetectionAttempted, userHasManuallySelected, currentLanguage])

  // Save language to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('selectedLanguage', currentLanguage)
    }
  }, [currentLanguage])

  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode)
    
    // Mark that user has manually selected a language
    setUserHasManuallySelected(true)
    if (typeof window !== 'undefined') {
      localStorage.setItem('userManualLanguageSelection', 'true')
      localStorage.setItem('selectedLanguage', langCode)
    }
  }

  const isRTL = () => {
    return rtlLanguages.includes(currentLanguage)
  }

  // Function to reset language preferences and re-attempt GPS detection
  const resetToGpsLanguage = async () => {
    console.log('🔄 Resetting to GPS language detection...')
    
    if (typeof window !== 'undefined') {
      localStorage.removeItem('userManualLanguageSelection')
      localStorage.removeItem('gpsLanguageDetected')
      localStorage.removeItem('gpsDetectedLanguage')
      localStorage.removeItem('selectedLanguage')
    }
    
    setUserHasManuallySelected(false)
    setGpsDetectionAttempted(false)
    setCurrentLanguage('en')
    
    // Re-attempt GPS detection immediately
    setTimeout(async () => {
      try {
        console.log('🌍 Re-attempting GPS detection...')
        const detectedLanguage = await detectUserLanguage()
        console.log('🎯 Re-detection result:', detectedLanguage)
        
        if (detectedLanguage && detectedLanguage !== 'en') {
          console.log('✨ Setting language to:', detectedLanguage)
          setCurrentLanguage(detectedLanguage)
          localStorage.setItem('selectedLanguage', detectedLanguage)
          localStorage.setItem('gpsLanguageDetected', 'true')
          localStorage.setItem('gpsDetectedLanguage', detectedLanguage)
        }
      } catch (error) {
        console.warn('❌ Failed to re-detect language:', error)
      }
    }, 100)
  }

  // Force GPS detection regardless of conditions
  const forceGpsDetection = async () => {
    console.log('🔥 Forcing GPS detection...')
    try {
      const detectedLanguage = await detectUserLanguage()
      console.log('🎯 Forced detection result:', detectedLanguage)
      
      if (detectedLanguage && detectedLanguage !== 'en') {
        console.log('✨ Setting language to:', detectedLanguage)
        setCurrentLanguage(detectedLanguage)
        localStorage.setItem('selectedLanguage', detectedLanguage)
        localStorage.setItem('gpsLanguageDetected', 'true')
        localStorage.setItem('gpsDetectedLanguage', detectedLanguage)
      }
      return detectedLanguage
    } catch (error) {
      console.warn('❌ Forced GPS detection failed:', error)
      throw error
    }
  }

  // Check if current language was detected via GPS
  const isGpsDetectedLanguage = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('gpsLanguageDetected') === 'true'
    }
    return false
  }

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        languages,
        t,
        changeLanguage,
        isRTL,
        resetToGpsLanguage,
        forceGpsDetection,
        isGpsDetectedLanguage,
        userHasManuallySelected,
        gpsDetectionAttempted
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}
